import React from "react";
import { Input } from "@/components/ui/input";
import { lineEmoji, productLabel, shiftLabel } from "@/lib/ui";
import { Search, X } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dayName = (s) => new Date(s + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long" });

export const EMPTY = { days: [], line: "", product: "", q: "" };

export const weekDays = (anchor, week) => {
  if (!anchor || week == null) return [];
  const s = new Date(anchor + "T00:00:00");
  s.setDate(s.getDate() + (week - 1) * 7);
  return [...Array(7)].map((_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return iso(d); });
};

export const allocLabel = (a, pmap) => (a.product_id ? productLabel(pmap[a.product_id] || { code: a.code, name: a.name }) : `Reason: ${a.reason || ""}`);

export function matchesFilters(e, f, pmap) {
  const allocs = e.allocations || [];
  if (f.days.length && !f.days.includes(e.date)) return false;
  if (f.line && e.line !== f.line) return false;
  if (f.product === "__reason" && !allocs.some((a) => !a.product_id && a.reason)) return false;
  if (f.product && f.product !== "__reason" && !allocs.some((a) => a.product_id === f.product)) return false;
  const words = f.q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = [e.line, shiftLabel(e.shift), e.date, dayName(e.date), e.saved_by_name, e.later_reason,
    ...(e.workers || []).map((w) => w.name), ...allocs.flatMap((a) => [allocLabel(a, pmap), a.code]),
    ...(e.photos || []).flatMap((p) => [p.caption, p.filename])].filter(Boolean).join(" ").toLowerCase();
  return words.every((w) => text.includes(w));
}

const chip = (on) => `shrink-0 rounded-full px-3 h-9 text-xs font-semibold border transition-colors ${on ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`;

function DayChips({ days, counts, f, set }) {
  const toggle = (d) => set({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] });
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="photo-filter-days">
      <button data-testid="photo-day-all" onClick={() => set({ ...f, days: [] })} className={chip(!f.days.length)}>All days</button>
      {days.map((d) => {
        const dt = new Date(d + "T00:00:00");
        return (
          <button key={d} data-testid={`photo-day-${d}`} onClick={() => toggle(d)} className={`${chip(f.days.includes(d))} ${counts[d] ? "" : "opacity-50"}`}>
            {dt.toLocaleDateString("en-AU", { weekday: "short" })} {dt.getDate()} <span className="opacity-70">({counts[d] || 0})</span>
          </button>
        );
      })}
    </div>
  );
}

export function PhotoFilters({ shots, days, pmap, f, set, shown }) {
  const counts = shots.reduce((m, e) => ({ ...m, [e.date]: (m[e.date] || 0) + 1 }), {});
  const lines = [...new Set(shots.map((e) => e.line))].sort();
  const prods = new Map();
  let reasons = false;
  shots.forEach((e) => (e.allocations || []).forEach((a) => { if (a.product_id) prods.set(a.product_id, allocLabel(a, pmap)); else if (a.reason) reasons = true; }));
  const active = f.days.length || f.line || f.product || f.q;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 space-y-2.5" data-testid="photo-filters">
      <DayChips days={days} counts={counts} f={f} set={set} />
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="photo-filter-lines">
        <button data-testid="photo-line-all" onClick={() => set({ ...f, line: "" })} className={chip(!f.line)}>All lines</button>
        {lines.map((l) => <button key={l} data-testid={`photo-line-${l.replace(/[ /]/g, "-")}`} onClick={() => set({ ...f, line: f.line === l ? "" : l })} className={chip(f.line === l)}>{lineEmoji(l)} {l}</button>)}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <select data-testid="photo-filter-product" value={f.product} onChange={(e) => set({ ...f, product: e.target.value })} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm sm:w-60">
          <option value="">All products</option>
          {reasons && <option value="__reason">Other (written reason)</option>}
          {[...prods.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input data-testid="photo-filter-search" value={f.q} onChange={(e) => set({ ...f, q: e.target.value })} placeholder="Search products, reasons, captions, supervisor, crew…" className="pl-9 h-11 rounded-xl" />
        </div>
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span data-testid="photo-filter-count">Showing {shown.length} of {shots.length} logs · {shown.reduce((s, e) => s + e.photos.length, 0)} photos</span>
        {active ? <button data-testid="photo-filter-clear" onClick={() => set(EMPTY)} className="inline-flex items-center gap-1 font-semibold text-teal-700"><X size={13} /> Clear filters</button> : null}
      </div>
    </div>
  );
}
